




import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Package,
  CreditCard,
  CheckCircle2,
  Truck,
  RotateCcw,
  Eye,
  ArrowRight,
  Receipt,
  Sparkles,
  Star,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../redux/store";
import {
  listenToClassifiedGuestOrders,
  upsertClassifiedOrder,
} from "../../redux/classifiedOrdersSlice";

/* ─── PALETTE (mirrors GlamClientOrders exactly) ─── */
const G = {
  black:    "#000000",
  ink:      "#080808",
  dark:     "#0f0f0f",
  panel:    "#141414",
  border:   "#1e1e1e",
  white:    "#ffffff",
  offwhite: "#ede8df",
  muted:    "#4a4a4a",
  dim:      "#282828",
  gold:     "#a8894a",
  goldLt:   "#bfa06a",
  goldDim:  "#4a3c20",
  goldGlow: "rgba(168,137,74,0.08)",
  success:  "#2a6640",
  warn:     "#7a5010",
  blue:     "#3a3a6a",
};

/* ─── HELPERS ─── */
const fmt = (n?: number) =>
  !n || isNaN(n) ? "0.00" : Number(n).toFixed(2);

const getItemBadge = (status: any) => {
  if (status?.orderDelivered)  return { label: "Delivered",  color: G.gold,    dot: G.success };
  if (status?.sellerDelivered) return { label: "In Transit", color: "#7a6030", dot: G.warn    };
  return                              { label: "Processing", color: "#4a4a7a", dot: G.blue    };
};

const getOrderStage = (o?: any) => {
  if (o?.isConfirmed) return { label: "Confirmed", color: G.gold  };
  return                     { label: "Pending",   color: G.muted };
};

/* ─── THIN DIVIDER ─── */
const Hairline = ({ my = 16 }: { my?: number }) => (
  <div style={{
    margin: `${my}px 0`,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim} 40%, ${G.goldDim} 60%, transparent)`,
  }} />
);

/* ─── ACCORDION ─── */
function Accordion({
  icon: Icon,
  title,
  open,
  onToggle,
  children,
}: {
  icon: React.ElementType;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      border: `1px solid ${open ? G.goldDim : G.border}`,
      marginBottom: 10,
      overflow: "hidden",
      transition: "border-color 0.3s",
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "15px 18px",
          background: open ? "rgba(168,137,74,0.04)" : G.panel,
          border: "none",
          cursor: "pointer",
          transition: "background 0.25s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon size={13} color={open ? G.gold : G.muted} strokeWidth={1.5} />
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 13,
            letterSpacing: 2.5,
            textTransform: "uppercase" as const,
            fontWeight: 600,
            color: open ? G.offwhite : G.muted,
          }}>
            {title}
          </span>
        </div>
        {open
          ? <ChevronUp   size={14} color={G.gold}  strokeWidth={1.5} />
          : <ChevronDown size={14} color={G.muted} strokeWidth={1.5} />
        }
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "18px 20px", background: G.dark }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── ORDER ITEM ROW ─── */
function OrderItemRow({
  item,
  order,
  listingsMap,
  dispatch,
  navigate,
}: {
  item: any;
  order: any;
  listingsMap: Record<string, any>;
  dispatch: AppDispatch;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const status = item.deliveryStatus || {};
  const badge = getItemBadge(status);
  const isFullyDelivered =
    status.sellerDelivered && status.buyerReceived && status.orderDelivered;
  const canReceive = status.sellerDelivered && !status.buyerReceived;

  const prod = listingsMap[item.listingId] || {};
  const vImg = prod?.variants?.[item.colorId]?.imageUrl;
  const img  = vImg || prod?.photos?.[0] || item.image || "/default-product.jpg";

  return (
    <div style={{
      display: "flex",
      gap: 14,
      padding: "14px 0",
      borderBottom: `1px solid ${G.border}`,
    }}>
      {/* Thumbnail */}
      <div style={{
        width: 58,
        height: 58,
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
      }}>
        <img
          src={img}
          alt={prod?.name || item.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.85)" }}
          onError={(e) => ((e.target as HTMLImageElement).src = "/default-product.jpg")}
        />
        <div style={{ position: "absolute", inset: 0, border: `1px solid ${G.border}` }} />
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 14,
            fontWeight: 700,
            color: G.offwhite,
            lineHeight: 1.2,
          }}>
            {prod?.name || item.name}
          </span>
          <span style={{
            fontFamily: "monospace",
            fontSize: 13,
            color: G.goldLt,
            fontWeight: 600,
            flexShrink: 0,
            opacity: 0.8,
          }}>
            €{fmt((prod?.rate ?? item.rate ?? 0) * item.qty)}
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" as const }}>
          {item.size && item.size !== "–" && (
            <span style={{ fontSize: 10, color: G.muted, letterSpacing: 1, textTransform: "uppercase" as const }}>
              {item.size}
            </span>
          )}
          {item.colour && (
            <span style={{ fontSize: 10, color: G.muted, letterSpacing: 1, textTransform: "uppercase" as const }}>
              {item.colour}
            </span>
          )}
          <span style={{ fontSize: 10, color: G.muted }}>×{item.qty}</span>
        </div>

        {/* Status dot */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: badge.dot }} />
          <span style={{ fontSize: 10, color: badge.color, letterSpacing: 1.5, textTransform: "uppercase" as const, fontWeight: 700 }}>
            {badge.label}
          </span>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" as const }}>
          {!status.buyerReceived && (
            <ActionBtn
              label="Received"
              icon={CheckCircle2}
              active={canReceive}
              onClick={() => {
                if (!canReceive) return;
                dispatch(
                  upsertClassifiedOrder({
                    paymentId: order.id,
                    listingId: item.listingId,
                    deliveryStatus: {
                      sellerDelivered: true,
                      buyerReceived: true,
                      orderDelivered: true,
                    },
                  })
                );
              }}
            />
          )}
          {isFullyDelivered && (
            <ActionBtn
              label="Review"
              icon={Star}
              active
              onClick={() =>
                navigate(`/ClassifiedProductsReview/${order.guestId}/${item.listingId}/${order.id}`)
              }
            />
          )}
          <ActionBtn
            label="Track"
            icon={Truck}
            active
            onClick={() => navigate(`/track/${order.id}/${item.listingId}`)}
            ghost
          />
        </div>
      </div>
    </div>
  );
}

/* ─── TINY ACTION BUTTON ─── */
function ActionBtn({
  label, icon: Icon, active, onClick, ghost = false,
}: {
  label: string;
  icon: React.ElementType;
  active: boolean;
  onClick: () => void;
  ghost?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      disabled={!active}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 12px",
        fontSize: 10,
        letterSpacing: 1.2,
        fontWeight: 700,
        textTransform: "uppercase" as const,
        border: `1px solid ${active ? (ghost ? G.border : G.goldDim) : G.dim}`,
        background: hover && active ? G.goldGlow : "transparent",
        color: active ? (ghost ? G.muted : G.gold) : G.dim,
        cursor: active ? "pointer" : "not-allowed",
        transition: "all 0.2s",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <Icon size={10} strokeWidth={2} />
      {label}
    </button>
  );
}

/* ─── REORDER BUTTON ─── */
function ReorderBtn({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: "100%",
        padding: "14px",
        background: hover ? G.goldGlow : "transparent",
        border: `1px solid ${G.goldDim}`,
        color: G.gold,
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 3,
        textTransform: "uppercase" as const,
        cursor: "pointer",
        marginBottom: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        transition: "all 0.25s",
        opacity: 0.85,
      }}
    >
      <RotateCcw size={12} strokeWidth={2} />
      Reorder
      <ArrowRight size={12} strokeWidth={2} />
    </button>
  );
}

/* ─── FONT LOADER ─── */
const GoogleFonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-thumb { background: #1e1e1e; }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */

export default function ClassifiedOrders() {
  const { userId } = useParams<{ userId: string }>();
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();

  const orders   = useSelector((s: RootState) => s.classifiedOrders.orders);
  const listings = useSelector((s: RootState) => s.classifieds.listings);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(() => {
    if (userId) dispatch(listenToClassifiedGuestOrders(userId));
  }, [userId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l) => (m[l.listingId] = l));
    return m;
  }, [listings]);

  /* Most recent non-fully-delivered order */
  const order = [...orders]
    .sort((a, b) =>
      new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    )
    .find((o) => !o.items?.every((i: any) =>
      i.deliveryStatus?.sellerDelivered &&
      i.deliveryStatus?.buyerReceived &&
      i.deliveryStatus?.orderDelivered
    )) ?? null;

  const allDelivered =
    !order ||
    (order.items?.length > 0 &&
      order.items.every((i: any) =>
        i.deliveryStatus?.sellerDelivered &&
        i.deliveryStatus?.buyerReceived &&
        i.deliveryStatus?.orderDelivered
      ));

  /* ── Empty state ── */
  if (allDelivered || !order) {
    return (
      <div style={S.page}>
        <GoogleFonts />
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: "center", maxWidth: 380 }}
        >
          <ShoppingBag size={36} color={G.goldDim} strokeWidth={1} style={{ margin: "0 auto 20px", display: "block" }} />
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, color: G.offwhite, fontWeight: 400, letterSpacing: 3, marginBottom: 10 }}>
            No Active Orders
          </h3>
          <p style={{ color: G.muted, fontSize: 12, marginBottom: 28, letterSpacing: 0.5 }}>
            Your curated collection awaits
          </p>
          <button
            onClick={() => navigate("/shop")}
            style={S.primaryBtn}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.85")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "1")}
          >
            Explore Collection <ArrowRight size={13} />
          </button>
        </motion.div>
      </div>
    );
  }

  const firstItem = order.items?.[0];
  const firstListing = listingsMap[firstItem?.listingId] || {};
  const vImg = firstListing?.variants?.[firstItem?.colorId]?.imageUrl;
  const heroPhoto = vImg || firstListing?.photos?.[0] || firstItem?.image || "/default-product.jpg";
  const heroName  = firstListing?.name || firstItem?.name || "Your Order";

  const stage = getOrderStage(order);

  return (
    <div style={S.page}>
      <GoogleFonts />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.2, 0, 0.1, 1] }}
        style={S.card}
      >
        {/* ── HEADER ── */}
        <div style={S.header}>
          <div style={S.headerRule}>
            <div style={S.ruleLine} />
            <Sparkles size={12} color={G.goldDim} strokeWidth={1.5} />
            <div style={S.ruleLine} />
          </div>
          <h1 style={S.title}>My Order</h1>
        </div>

        {/* ── HERO CARD ── */}
        <div style={S.heroCard}>
          <div style={S.goldBar} />
          <div style={{ display: "flex" }}>
            <div style={S.heroImgWrap}>
              <img
                src={heroPhoto}
                alt={heroName}
                style={S.heroImg}
                onError={(e) => ((e.target as HTMLImageElement).src = "/default-product.jpg")}
              />
              <div style={S.heroImgFade} />
            </div>
            <div style={S.heroInfo}>
              <div>
                <div style={S.heroName}>
                  {heroName}
                  {order.items.length > 1 && (
                    <span style={S.heroMore}> +{order.items.length - 1}</span>
                  )}
                </div>
                <div style={S.heroId}>{order.orderId || order.id}</div>
              </div>
              <div style={{
                ...S.badge,
                color: stage.color,
                borderColor: stage.color === G.gold ? G.goldDim : G.dim,
              }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: stage.color, opacity: 0.7 }} />
                {stage.label}
              </div>
            </div>
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={S.statsGrid}>
          {([
            { label: "Total Paid",  value: `€${fmt(order.totalPaid)}`, Icon: CreditCard },
            { label: "Items",       value: String(order.totalQty),      Icon: Package   },
            { label: "Service Fee", value: `€${fmt(order.guestsFee)}`, Icon: Receipt   },
          ] as const).map(({ label, value, Icon }, i) => (
            <div key={i} style={{ ...S.statCell, borderRight: i < 2 ? `1px solid ${G.border}` : "none" }}>
              <Icon size={12} color={G.goldDim} strokeWidth={1.5} style={{ marginBottom: 5, display: "block", margin: "0 auto 5px" }} />
              <div style={S.statLabel}>{label}</div>
              <div style={S.statValue}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── REORDER ── */}
        <ReorderBtn onClick={() => navigate(`/generalcommercedetails/${firstItem?.listingId}`)} />

        {/* ── ORDER DETAILS ── */}
        <Accordion
          icon={Eye}
          title="Order Details"
          open={detailsOpen}
          onToggle={() => { setDetailsOpen(o => !o); setPaymentOpen(false); }}
        >
          {order.items?.map((item: any, idx: number) => (
            <OrderItemRow
              key={(item.listingId || idx) + idx}
              item={item}
              order={order}
              listingsMap={listingsMap}
              dispatch={dispatch}
              navigate={navigate}
            />
          ))}
        </Accordion>

        {/* ── PAYMENT ── */}
        <Accordion
          icon={CreditCard}
          title="Payment Summary"
          open={paymentOpen}
          onToggle={() => { setPaymentOpen(o => !o); setDetailsOpen(false); }}
        >
          {[
            { label: "Subtotal",    value: `€${fmt(order.subtotal ?? order.totalPaid - order.guestsFee)}` },
            { label: "Service Fee", value: `€${fmt(order.guestsFee)}` },
            { label: "Delivery",    value: order.deliveryFee ? `€${fmt(order.deliveryFee)}` : "Complimentary" },
          ].map(({ label, value }, i) => (
            <div key={i} style={S.payRow}>
              <span style={{ fontSize: 12, color: G.muted, letterSpacing: 0.3 }}>{label}</span>
              <span style={{ fontFamily: "monospace", fontSize: 12, color: G.offwhite }}>{value}</span>
            </div>
          ))}
          <Hairline my={14} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 700, color: G.offwhite, letterSpacing: 1.5, textTransform: "uppercase" }}>
              Total Paid
            </span>
            <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: G.gold, opacity: 0.9 }}>
              €{fmt(order.totalPaid)}
            </span>
          </div>
        </Accordion>

        {/* ── FOOTER ── */}
        <div style={S.footer}>
          <p style={S.footerBrand}>Classified · Est. 2024</p>
          <p style={S.footerLink}>
            Questions?{" "}
            <span style={{ color: G.gold, opacity: 0.7, cursor: "pointer" }}>
              Contact Support
            </span>
          </p>
        </div>

      </motion.div>
    </div>
  );
}

/* ─── STATIC STYLES ─── */
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: G.black,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 16px 60px",
    fontFamily: "'Montserrat', sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 520,
  },
  header: {
    textAlign: "center",
    marginBottom: 28,
  },
  headerRule: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 10,
  },
  ruleLine: {
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim})`,
    maxWidth: 80,
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 26,
    fontWeight: 400,
    color: G.offwhite,
    letterSpacing: 6,
    textTransform: "uppercase",
  },
  heroCard: {
    background: G.ink,
    border: `1px solid ${G.border}`,
    marginBottom: 10,
    overflow: "hidden",
  },
  goldBar: {
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim} 40%, ${G.goldDim} 60%, transparent)`,
  },
  heroImgWrap: {
    width: 110,
    height: 110,
    flexShrink: 0,
    overflow: "hidden",
    position: "relative",
  },
  heroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    filter: "brightness(0.75) saturate(0.8)",
  },
  heroImgFade: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(90deg, transparent 50%, rgba(8,8,8,0.85))",
  },
  heroInfo: {
    flex: 1,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  heroName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 16,
    fontWeight: 700,
    color: G.offwhite,
    lineHeight: 1.25,
    marginBottom: 5,
  },
  heroMore: {
    fontSize: 11,
    color: G.muted,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 400,
  },
  heroId: {
    fontSize: 9,
    color: G.muted,
    letterSpacing: 1.5,
    textTransform: "uppercase",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    border: "1px solid",
    width: "fit-content",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    border: `1px solid ${G.border}`,
    marginBottom: 10,
    background: G.ink,
  },
  statCell: {
    padding: "14px 10px",
    textAlign: "center",
  },
  statLabel: {
    fontSize: 9,
    color: G.muted,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  statValue: {
    fontSize: 14,
    fontWeight: 700,
    color: G.offwhite,
    fontFamily: "monospace",
  },
  payRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "9px 0",
    borderBottom: `1px solid ${G.border}`,
  },
  primaryBtn: {
    padding: "13px 30px",
    background: "transparent",
    border: `1px solid ${G.goldDim}`,
    color: G.gold,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 2.5,
    textTransform: "uppercase",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    opacity: 0.85,
    transition: "opacity 0.2s",
  },
  footer: {
    textAlign: "center",
    marginTop: 28,
    paddingTop: 18,
    borderTop: `1px solid ${G.border}`,
  },
  footerBrand: {
    fontSize: 9,
    color: G.dim,
    letterSpacing: 3,
    textTransform: "uppercase",
  },
  footerLink: {
    fontSize: 10,
    color: G.muted,
    marginTop: 5,
  },
};